'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const VideoComponent = ({
  file,
  loop,
  autoplay,
  youtube_url,
  sound,
  restart,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const { ref, inView } = useInView({
    rootMargin: '-50% 0% -50% 0%', // Shrink the bottom of the root by 100px
    triggerOnce: true, // Only trigger once when the element comes into view
    threshold: 0,
  });

  const handleVideoEnd = () => {
    setIsPlaying(false); // Update the state when the video ends
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  useEffect(() => {
    const video = videoRef.current;

    if (autoplay && inView && video) {
      video.play();
      setIsPlaying(true);
    }

    // Add event listener for the 'ended' event
    if (video) {
      video.addEventListener('ended', handleVideoEnd);
    }

    // Cleanup the event listener on unmount
    return () => {
      if (video) {
        video.removeEventListener('ended', handleVideoEnd);
      }
    };
  }, [autoplay, inView]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0; // Reset video to the start
      if (autoplay && inView) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [restart]);

  return (
    <div ref={ref} className="w-full h-full relative">
      {youtube_url ? (
        <div className="relative w-full h-full z-10">
          <iframe
            className="w-full h-full"
            src={`${youtube_url}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            disablePictureInPicture={true}
            disableRemotePlayback={true}
            playsInline
            loop={loop}
            muted={isMuted}
            controls={false}
            preload="auto"
          >
            <source
              src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${file}`}
              type="video/webm"
            />
            <source
              src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${file}`}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>

          <div className="absolute flex gap-2 bottom-3 z-10 right-3">
            {sound && (
              <div
                onClick={handleMuteUnmute}
                className="w-[45px] h-[45px] rounded-full bg-[#24272c] flex items-center justify-center cursor-pointer"
              >
                {isMuted ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    className="invert"
                    fill="#000000"
                    viewBox="0 0 256 256"
                  >
                    <path d="M64,84v88a4,4,0,0,1-4,4H32a16,16,0,0,1-16-16V96A16,16,0,0,1,32,80H60A4,4,0,0,1,64,84Zm93.15-58.15a8,8,0,0,0-10-.16l-65.57,51A4,4,0,0,0,80,79.84v96.32a4,4,0,0,0,1.55,3.15l65.57,51a8,8,0,0,0,9,.56,8.29,8.29,0,0,0,3.91-7.18V32.25A8.27,8.27,0,0,0,157.12,25.85Z"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="#000000"
                    viewBox="0 0 256 256"
                    className="invert"
                  >
                    <path d="M245.66,146.34a8,8,0,0,1-11.32,11.32L216,139.31l-18.34,18.35a8,8,0,0,1-11.32-11.32L204.69,128l-18.35-18.34a8,8,0,0,1,11.32-11.32L216,116.69l18.34-18.35a8,8,0,0,1,11.32,11.32L227.31,128ZM60,80H32A16,16,0,0,0,16,96v64a16,16,0,0,0,16,16H60a4,4,0,0,0,4-4V84A4,4,0,0,0,60,80Zm97.15-54.15a8,8,0,0,0-10-.16l-65.57,51A4,4,0,0,0,80,79.84v96.32a4,4,0,0,0,1.55,3.15l65.57,51a8,8,0,0,0,9,.56,8.29,8.29,0,0,0,3.91-7.18V32.25A8.27,8.27,0,0,0,157.12,25.85Z"></path>
                  </svg>
                )}
              </div>
            )}

            <div
              onClick={handlePlayPause}
              className="w-[45px] h-[45px] rounded-full bg-[#24272c] flex items-center justify-center cursor-pointer"
            >
              {!isPlaying ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  fill="#000000"
                  viewBox="0 0 256 256"
                  className="invert"
                >
                  <path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"></path>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  fill="#000000"
                  viewBox="0 0 256 256"
                  className="invert"
                >
                  <path d="M216,48V208a16,16,0,0,1-16,16H160a16,16,0,0,1-16-16V48a16,16,0,0,1,16-16h40A16,16,0,0,1,216,48ZM96,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16H96a16,16,0,0,0,16-16V48A16,16,0,0,0,96,32Z"></path>
                </svg>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoComponent;
